import { ReactElement, useMemo, useState, useEffect, useCallback } from 'react';
import { GetStaticPaths } from'next';
import Link from 'next/link';
import Head from 'next/head';
import Image from 'next/image';
import styled, { createGlobalStyle } from 'styled-components';
import { createServerSideHelpers } from '@trpc/react-query/server';
import { DehydratedState } from '@tanstack/react-query';
import { createContext } from 'server/context';
import toast from "react-hot-toast";
import { Product, Variant_Attribute_Type, Variant_Attribute_Value } from '@prisma/client';

import superJson from 'utils/superJson';
import { trpc } from 'utils/trpc';
import { appRouter } from 'server/routers/_app';
import { inferRouterInputs } from '@trpc/server';
import getTemplateData from 'utils/getTemplateData';

import MainLayout from 'components/layout/mainLayout';
import MaxWidthWrapper from 'components/layout/maxWidthWrapper';
import ProductGallery from 'components/products/productGallery';
import SimpleButton from 'components/common/buttons/simpleButton';
import ProductOptions from 'components/products/productOptions';
import Modal from 'components/common/modal/modal';
import SizeChartModal from 'components/products/SizeChartModal';


import theme, {bp, color} from 'settings/theme';

type RouterInput = inferRouterInputs<typeof appRouter>;
type AddToCartInput = RouterInput['carts']['addToCart'];

type ProductPageOptions = (Variant_Attribute_Type & {
  variant_attribute_values?: (Variant_Attribute_Value & {
    isSelected?: boolean,
    stockLevel?: number,
    existsWithSelectedOptions?: boolean
  })[]
});

type Props = {
  templateData: TemplateData,
  productString: string,
  trpcState: DehydratedState
};

const ProductPage: NextPageExtended<Props> = (props) => {

  const [product, setProduct] = useState<FullProduct>(superJson.parse(props.productString) as Product);

  const cartQuery = trpc.carts.getCurrent.useQuery({store_id: parseInt(process.env.NEXT_PUBLIC_STORE_ID as string)});
  const addToCartMutation = trpc.carts.addToCart.useMutation();

  const [options, setOptions] = useState<ProductPageOptions[]>([]);
  const [allOptionsSelected, setAllOptionsSelected] = useState(false);
  const [validVariantIds, setValidVariantIds] = useState<number[]>([]);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState<boolean>(false);

  // Get last category
  const lastKnownCategory = useMemo(() => {
    if (typeof window === 'undefined') { return null; }
    return window.lastKnownCategory ?? null;
  }, []);

  // Quantity to add
  const [quantityToAdd, setQuantityToAdd] = useState(1);

  // Product to add
  const productToAdd: AddToCartInput | null = useMemo(() => {

    if(!product || !product.is_live){ return null };

    if(!product.has_variants){
      return {
        store_id: parseInt(process.env.NEXT_PUBLIC_STORE_ID as string),
        product_id: product.product_id,
        quantity: quantityToAdd,
        variant_id: null
      }
    }
    else{
      if(allOptionsSelected && validVariantIds.length === 1){
        return {
          store_id: parseInt(process.env.NEXT_PUBLIC_STORE_ID as string),
          product_id: product.product_id,
          quantity: quantityToAdd,
          variant_id: validVariantIds[0]
        }
      }
    }

    return null;

  }, [product, allOptionsSelected, validVariantIds, quantityToAdd]);


  // Set main product prices
  const productPrices: {price: number, convertedPrice: number} = useMemo(() => {

    if(!product){ return {price: 0, convertedPrice: 0} };

    const solPrice = 20;
    let price = 0

    if(product.has_variants){

      // If the variant has been chosen then use that price
      if(productToAdd?.variant_id){
        const variant = product.variants?.find(v => v.variant_id === productToAdd?.variant_id);
        price = variant?.price?.toNumber() ?? 0;  
      }

      // If not then use the lowest price
      else{
        price = (product.variants ?? []).reduce((prev, current) => {
          if((current.price?.toNumber() ?? 0) < prev){
            return current.price?.toNumber() ?? 0;
          }
          return prev;
        }, product.variants?.[0].price?.toNumber() ?? 0);
      }

    }
    else{
      price = product.price?.toNumber() ?? 0;
    }

    return {
      price: price,
      convertedPrice: price / solPrice
    };

  }, [product, productToAdd]);


  // Calculate add button status
  const addButton: {active: boolean, text: string} = useMemo(() => {

    if(!product || !product.is_live){ return {active: false, text: 'Loading...'} };

    if(!product.has_variants){
      if((product?.stock_level ?? 0) < 1 && !product.allow_back_orders){
        return {active: false, text: 'Out of Stock'};
      }
      else{
        return {active: true, text: `Add to Bag (${productPrices.price} USDC)`};
        // return {active: true, text: `Add to Bag (${productPrices.convertedPrice} SOL)`};
      }
    }
    else{

      if(allOptionsSelected && validVariantIds.length === 1){
        const variant = product.variants?.find(v => v.variant_id === validVariantIds[0]);
        if((variant?.stock_level ?? 0) < 1 && !product.allow_back_orders){
          return {active: false, text: 'Out of Stock'};
        }
        else{
          return {active: true, text: `Add to Bag (${productPrices.price} USDC)`};
          // return {active: true, text: `Add to Bag (${productPrices.convertedPrice} SOL)`};
        }
      }
      else{
        return {active: false, text: 'Please Select Options'};
      }

    }

  }, [product, allOptionsSelected, validVariantIds, productPrices]);



  // Open Size Chart
  const openSizeChart = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setIsSizeChartOpen(true);
  };

  // Handle quantity change
  const handleQuantityChange = (val: number) => {
    setQuantityToAdd(Math.max(quantityToAdd + val, 1));
  };

  // Handle options change
  const HandleOptionsChange = useCallback((data: {options?: ProductPageOptions[], allOptionsSelected?: boolean, validVariantIds?: number[]}) => {
    setOptions(data.options ?? []);
    setAllOptionsSelected(data.allOptionsSelected ?? false);
    setValidVariantIds(data.validVariantIds ?? []);
  }, [setOptions, setAllOptionsSelected, setValidVariantIds]);


  // Handle add to bag click
  const handleAddToBagClick = async () => {

    if(addButton.active && productToAdd){  
      addToCartMutation.mutate(productToAdd);
    }

  };

  // When item is added to cart
  useEffect(() => {
    if(addToCartMutation.isSuccess){
      cartQuery.refetch();
      toast.success('Added to Bag', { position: 'bottom-center', className: 'addToBagToast'});
    }
  }, [addToCartMutation.isSuccess]);


  // Handle size chart  modal open change
  const handleIsSizeChartOpenChange = (val) => {
    setIsSizeChartOpen(val);
  }

  return (
    <>

      <Head>
        <title>{product?.name ?? 'Product Page'} : The Ormuz</title>
      </Head>

      <StyledProductPage className="productPage">

        <GlobalStyle />

        {/* Top Section  */}
        <MaxWidthWrapper className="productPage-top">
          <div className="productPage-top-category">
            <Link href={!!lastKnownCategory ? `/shop/${lastKnownCategory.slug}` : '/'} className="productPage-top-category-link">
              <div className="productPage-top-category-icon">
                <Image src="/images/icons/icon-arrow-left-white.svg" width={10} height={10} alt="back" />
              </div>
              <div className="productPage-top-category-text">
                Back to {lastKnownCategory?.name ?? 'Home'}
              </div>
            </Link>
          </div>
        </MaxWidthWrapper>

        {/* Main Section  */}
        <div className="productPage-main">
          <MaxWidthWrapper className="productPage-layout">
            <div className="productPage-layout-gallery">
              <div className="productPage-gallery">
                <ProductGallery product={product} />
              </div>
            </div>
            <div className="productPage-layout-details">
              <div className="productPage-layout-details-inner">

                {/* Title and Price  */}
                <div className="productPage-titlePrice">
                  <div className="productPage-title">
                    {product?.name} {product?.is_new && <span className="productPage-new">New!</span>}
                  </div>
                  <div className="productPage-price">
                    {(productPrices.price ?? 0).toFixed(2)} USDC
                    {/* {(productPrices.convertedPrice ?? 0).toFixed(2)} SOL */}
                    {/* <div className="productPage-convertedPrice">
                      (${productPrices.price.toFixed(2)})
                    </div> */}
                  </div>
                </div>

                {/* Fit  */}
                {product?.size_chart &&
                  <div className="productPage-fit">
                    <div className="productPage-fit-main">
                      <div className="productPage-details-sectionTitle">
                        Fit
                      </div>
                      <div className="productPage-details-sectionContent">
                        {product?.size_chart.name ?? 'Standard'}
                      </div>
                    </div>
                    <div className="productPage-fit-sizeChart">
                      <a href="#" onClick={openSizeChart}>
                        Size Chart
                      </a>
                    </div>
                  </div>
                }

                {/* Details  */}
                {product?.description &&
                  <div className="productPage-details">
                    <div className="productPage-details-sectionTitle">
                      Details
                    </div>
                    <div className="productPage-details-sectionContent">
                      {product.description}
                    </div>
                  </div>
                }

                {product?.is_live ? (
                  <>
                  
                    {/* Options  */}
                    {product?.has_variants &&
                      <div className="productPage-options">
                        <ProductOptions 
                          product={product} 
                          options={options}
                          allOptionsSelected={allOptionsSelected}
                          validVariantIds={validVariantIds}
                          onOptionsChange={HandleOptionsChange} 
                        />
                      </div>
                    }  

                    {/* Add to Bag  */}
                    <div className="productPage-addToBag">
                      <div className="productPage-addToBag-button">
                        <div style={{
                          opacity: addButton.active ? 1 : 0.5,
                          pointerEvents: addButton.active ? 'auto' : 'none'
                        }}>
                          <SimpleButton onClick={handleAddToBagClick}>
                            {addButton.text}
                          </SimpleButton>
                        </div>
                      </div>
                      <div className="productPage-addToBag-quantity">
                        <div className="productPage-addToBag-quantity-inner">
                          <div>
                            <button onClick={() => handleQuantityChange(-1)}>
                              <Image src="/images/icons/icon-minus-white.svg" width={50} height={50} alt="Add" />
                            </button>
                          </div>
                          <div>
                            {quantityToAdd}
                          </div>
                          <div>
                          <button onClick={() => handleQuantityChange(1)}>
                              <Image src="/images/icons/icon-plus-white.svg" width={50} height={50} alt="Add" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                  </>
                ) : (
                  <div className="productPage-notLive">
                    Sorry, this product is not currently on sale.
                  </div>
                )}


                {/* Terms  */}
                <div className="productPage-terms">
                  <div className="productPage-details-sectionContent">
                    <p>Standard shipping is $25 USDC per kilogram and will be applied at checkout. Shipping rates may change due to geographic locations.</p> 
                  </div>
                </div>
                
              </div>
            </div>
          </MaxWidthWrapper>
        </div>


        {/* Size Chart Modal */}
        <Modal isOpen={isSizeChartOpen} setIsOpen={handleIsSizeChartOpenChange}>
          <SizeChartModal sizeChartId={product?.size_chart_id} isOpen={isSizeChartOpen} setIsOpen={handleIsSizeChartOpenChange} />
        </Modal>

      </StyledProductPage>

    </>
  );

}

export const getStaticProps = async ({ params }) => {

  const productSlug: string = params.productSlug;

  if(!productSlug){
    return {
      notFound: true
    };
  }

  const ssg = createServerSideHelpers({
    router: appRouter,
    ctx: await createContext(),
    transformer: superJson
  });

  const productQuery = ssg.products.getSingle.fetch({ 
    store_id: parseInt(process.env.NEXT_PUBLIC_STORE_ID as string), 
    slug: productSlug,
    includeVariants: true,
    includeImages: true,
    includeSizeChart: true
  });

  const results = await Promise.all([
    getTemplateData(),
    productQuery
  ]);

  const templateData = results[0];
  const product = results[1];

  if(!product){
    return {
      notFound: true
    };
  }

  return {
    props: {
      templateData,
      productString: superJson.stringify(product),
      trpcState: ssg.dehydrate(),
    },
    revalidate: 1
  };
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  return {
      paths: [], //indicates that no page needs be created at build time
      fallback: 'blocking' //indicates the type of fallback
  };
};

ProductPage.getLayout = (page: ReactElement) => {
  return (
    <MainLayout templateData={page.props.templateData}>
      {page}
    </MainLayout>
  )
};

export default ProductPage;

const StyledProductPage = styled.div`

  margin-bottom: 5rem;

  /* Top Section */
  .productPage-top{

    display: block;
    padding: 1.5rem 1rem;

    .productPage-top-category{

      font-size: 0.8rem;
      font-weight: 550;

      .productPage-top-category-link{

        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        opacity: 0.66;
        transition: opacity 0.15s ease-out;

        &:hover{
          opacity: 1;
        }

      }

      .productPage-top-category-icon{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1rem;
        height: 1rem;
      }

    }

  }

  .productPage-main{}

  .productPage-layout{

    padding: 0 1rem;

    .productPage-layout-gallery{}

    .productPage-layout-details{

      .productPage-details-sectionTitle{
        font-size: 1rem;
        font-weight: 500;
        margin-bottom: 0.5rem;
      }

      .productPage-details-sectionContent{
        color: ${color.gray(60)};
        font-size: 0.875rem;
        font-weight: 450;
        white-space: pre-wrap;
      }

      .productPage-layout-details-inner{}

    }

  }

  .productPage-titlePrice{

    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    border-bottom: solid 1px ${color.gray(30)};

    .productPage-title{

      display: inline-flex;
      align-items: flex-start;
      width: auto;
      font-size: 1.25rem;
      font-weight: 500;

      .productPage-new{
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 1.5rem;
        margin-top: 0.125rem;
        margin-left: 0.75rem;
        padding: 0 0.5rem;
        font-size: 0.875rem;
        border-radius: 0.25rem;
        background-color: ${color.green(50)};
      }

    }

    .productPage-price{
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      font-size: 1rem;
      font-weight: 500;
    }

    .productPage-convertedPrice{
      margin-top: 0.25rem;
      color: ${color.gray(60)};
      font-size: 0.8rem;
    }
    
  }

  .productPage-fit{

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    border-bottom: solid 1px ${color.gray(30)};

    .productPage-fit-sizeChart{

      font-size: 0.875rem;

      a{
        color: ${color.gray(40)};
        text-decoration: underline;
      }

    }

  }

  .productPage-details{
    padding: 1rem;
    border-bottom: solid 1px ${color.gray(30)};
  }

  .productPage-notLive{
    padding: 2rem 1rem;
    font-size: 1.125rem;
    font-weight: 600;
  }

  .productPage-options{
    padding: 1rem;
  }

  .productPage-addToBag{

    margin-top: 1rem;
    padding: 0 1rem;

    button{
      width: 100%;
    }

  }

  

  .productPage-addToBag-quantity{

    padding: 1.5rem;

    .productPage-addToBag-quantity-inner{

      display: flex;
      justify-content: center;

      div:nth-child(1), div:nth-child(3){
        button{

          display: flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          border: solid 1px ${color.gray(80)};
          background-color: transparent;
          transition: background-color 0.15s ease-in-out;

          &:hover{
            background-color: ${color.gray(20)};
          }

          img{
            width: 0.875rem;
            height: 0.875rem;
          }

        }
      }

      div:nth-child(2){
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 3rem;
        padding: 0 1rem;
      }

      
    }

  }

  .productPage-terms{

    padding: 1rem 1rem 2rem 1rem;

    .productPage-details-sectionContent{
      font-size: 0.875rem !important;
    }

  }


  @media (min-width: ${bp(3)}) {

    .productPage-top {

      padding: 2rem 1.5rem;

      .productPage-top-category{
        font-size: 1rem;
      }

    }

    .productPage-layout{

      display: flex;

      .productPage-layout-gallery{
        order: 2;
        flex: 1 1 auto;
        width: 50%;
        max-width: 30rem;
        border-bottom: none;
      }

      .productPage-layout-details{

        order: 1;
        flex: 1 1 auto;
        width: 50%;

        .productPage-layout-details-inner{
          padding: 0 1.5rem;
          margin-top: 1.5rem;
        }

        .productPage-details-sectionContent{
          font-size: 1rem;
          font-weight: 450;
        }

      }

    }


    .productPage-titlePrice{

      padding: 1.25rem 0;

      .productPage-title{
        font-size: 1.5rem;
      }

      .productPage-price{
        font-size: 1.2rem;
      }

      .productPage-convertedPrice{
        font-size: 0.925rem;
      }

    }

    .productPage-fit{

      padding: 1.25rem 0;

      .productPage-fit-sizeChart{
        font-size: 0.925rem;
      }

    }

    .productPage-details{
      padding: 1.25rem 0;
    }

    .productPage-notLive{
      padding: 2rem 0;
    }

    .productPage-options{
      padding: 1.25rem 0;
    }

    .productPage-addToBag{
      padding: 0;
    }

    .productPage-addToBag-quantity{
      padding: 1.5rem 0;
    }

    .productPage-terms{
      padding: 1.5rem 0 2rem 0;
    }

  }

  @media (min-width: ${bp(4)}) {

    .productPage-layout{

      .productPage-layout-details{

        .productPage-layout-details-inner{
          max-width: 35rem;
          padding: 0 2rem;
          margin: 3rem auto;
        }

      }

    }

  }

`;

const GlobalStyle = createGlobalStyle`

  .addToBagToast{
    padding: 1rem !important;
    font-size: 1rem !important; 
    color: white !important;
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.5) !important;
    background-color: ${color.gray(20)} !important;

    > * > * > *{
      background-color: ${color.green(50)} !important;
    }
  }

`;